import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../App";
import { useCurrency } from "../contexts/CurrencyContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, Package } from "lucide-react";

var API_URL = process.env.REACT_APP_BACKEND_URL;

var assetTypes = ["furniture", "appliance", "fixture", "equipment"];
var conditions = ["excellent", "good", "fair", "poor"];

var emptyForm = {
  name: "",
  asset_type: "furniture",
  property_id: "",
  unit_id: "",
  description: "",
  purchase_date: "",
  purchase_price: "",
  condition: "good",
  serial_number: "",
  supplier_id: "",
  maintenance_party: "",
};

export default function Assets() {
  var { user, token } = useAuth();
  var { t } = useTranslation();
  var { formatPrice } = useCurrency();
  var [assets, setAssets] = useState([]);
  var [properties, setProperties] = useState([]);
  var [units, setUnits] = useState([]);
  var [suppliers, setSuppliers] = useState([]);
  var [filterUnits, setFilterUnits] = useState([]);
  var [loading, setLoading] = useState(true);
  var [dialogOpen, setDialogOpen] = useState(false);
  var [editingAsset, setEditingAsset] = useState(null);
  var [form, setForm] = useState(emptyForm);
  var [search, setSearch] = useState("");
  var [filterProperty, setFilterProperty] = useState("all");
  var [filterUnit, setFilterUnit] = useState("all");

  var canEdit = user?.role === "admin" || user?.role === "realestate_admin" || user?.role === "realestate_manager";
  var isAdmin = user?.role === "admin";

  useEffect(function() { fetchData(); }, []);

  // Fetch units when form property changes (for dialog)
  useEffect(function() {
    if (form.property_id && form.property_id !== "none") {
      fetchUnits(form.property_id);
    } else {
      setUnits([]);
      setForm(function(f) { return { ...f, unit_id: "" }; });
    }
  }, [form.property_id]);

  // Fetch units when filter property changes
  useEffect(function() {
    if (filterProperty && filterProperty !== "all") {
      fetchFilterUnits(filterProperty);
    } else {
      setFilterUnits([]);
      setFilterUnit("all");
    }
  }, [filterProperty]);

  async function fetchData() {
    try {
      var assetsRes = await fetch(API_URL + "/api/assets", {
        credentials: "include",
        headers: token ? { Authorization: "Bearer " + token } : {},
      });
      var propertiesRes = await fetch(API_URL + "/api/properties", {
        credentials: "include",
        headers: token ? { Authorization: "Bearer " + token } : {},
      });
      var suppliersRes = await fetch(API_URL + "/api/suppliers", {
        credentials: "include",
        headers: token ? { Authorization: "Bearer " + token } : {},
      });
      if (assetsRes.ok) setAssets(await assetsRes.json());
      if (propertiesRes.ok) setProperties(await propertiesRes.json());
      if (suppliersRes.ok) setSuppliers(await suppliersRes.json());
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }

  async function fetchUnits(propertyId) {
    try {
      var response = await fetch(API_URL + "/api/units?property_id=" + propertyId, {
        credentials: "include",
        headers: token ? { Authorization: "Bearer " + token } : {},
      });
      if (response.ok) {
        setUnits(await response.json());
      }
    } catch (error) {
      console.error("Error fetching units:", error);
    }
  }

  async function fetchFilterUnits(propertyId) {
    try {
      var response = await fetch(API_URL + "/api/units?property_id=" + propertyId, {
        credentials: "include",
        headers: token ? { Authorization: "Bearer " + token } : {},
      });
      if (response.ok) {
        setFilterUnits(await response.json());
      }
    } catch (error) {
      console.error("Error fetching filter units:", error);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    try {
      var url = editingAsset ? API_URL + "/api/assets/" + editingAsset.asset_id : API_URL + "/api/assets";
      var method = editingAsset ? "PUT" : "POST";

      var payload = {
        name: form.name,
        asset_type: form.asset_type,
        property_id: form.property_id === "none" || !form.property_id ? null : form.property_id,
        unit_id: form.unit_id === "none" || !form.unit_id ? null : form.unit_id,
        description: form.description || null,
        purchase_date: form.purchase_date || null,
        purchase_price: form.purchase_price ? parseFloat(form.purchase_price) : null,
        condition: form.condition,
        serial_number: form.serial_number || null,
        supplier_id: form.supplier_id === "none" || !form.supplier_id ? null : form.supplier_id,
        maintenance_party: form.maintenance_party || null,
      };

      var response = await fetch(url, {
        method: method,
        headers: { "Content-Type": "application/json", Authorization: token ? "Bearer " + token : "" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        toast.success(editingAsset ? t("common.success") : t("common.success"));
        setDialogOpen(false);
        setEditingAsset(null);
        setForm(emptyForm);
        fetchData();
      } else {
        var error = await response.json();
        toast.error(error.detail || t("common.error"));
      }
    } catch (error) {
      toast.error(t("common.error"));
    }
  }

  function handleEdit(asset) {
    setEditingAsset(asset);
    setForm({
      name: asset.name || "",
      asset_type: asset.asset_type || "furniture",
      property_id: asset.property_id || "",
      unit_id: asset.unit_id || "",
      description: asset.description || "",
      purchase_date: asset.purchase_date || "",
      purchase_price: asset.purchase_price ? String(asset.purchase_price) : "",
      condition: asset.condition || "good",
      serial_number: asset.serial_number || "",
      supplier_id: asset.supplier_id || "",
      maintenance_party: asset.maintenance_party || "",
    });
    // Fetch units for the selected property
    if (asset.property_id) {
      fetchUnits(asset.property_id);
    }
    setDialogOpen(true);
  }

  async function handleDelete(assetId) {
    if (!window.confirm(t("common.confirmDelete"))) return;
    try {
      var response = await fetch(API_URL + "/api/assets/" + assetId, {
        method: "DELETE",
        credentials: "include",
        headers: token ? { Authorization: "Bearer " + token } : {},
      });
      if (response.ok) {
        toast.success(t("common.success"));
        fetchData();
      } else {
        toast.error(t("common.error"));
      }
    } catch (error) {
      toast.error(t("common.error"));
    }
  }

  function getPropertyName(propertyId) {
    if (!propertyId) return "-";
    for (var i = 0; i < properties.length; i++) {
      if (properties[i].property_id === propertyId) return properties[i].name;
    }
    return "-";
  }

  function getUnitName(unitId) {
    if (!unitId) return "-";
    for (var i = 0; i < units.length; i++) {
      if (units[i].unit_id === unitId) return units[i].name;
    }
    return unitId.substring(0, 8) + "...";
  }

  function getSupplierName(supplierId) {
    if (!supplierId) return "-";
    for (var i = 0; i < suppliers.length; i++) {
      if (suppliers[i].supplier_id === supplierId) return suppliers[i].name;
    }
    return supplierId.substring(0, 8) + "...";
  }

  function renderSupplierOptions() {
    var items = [<SelectItem key="none" value="none">{t("common.none")}</SelectItem>];
    for (var i = 0; i < suppliers.length; i++) {
      var s = suppliers[i];
      if (s.is_active !== false) {
        items.push(<SelectItem key={s.supplier_id} value={s.supplier_id}>{s.name}</SelectItem>);
      }
    }
    return items;
  }

  function getConditionBadge(condition) {
    var conditionClasses = {
      excellent: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
      good: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
      fair: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
      poor: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
    };
    return <Badge className={conditionClasses[condition] || ""}>{t("assets.conditions." + condition)}</Badge>;
  }

  function getFilteredAssets() {
    var result = [];
    var searchLower = search.toLowerCase();
    for (var i = 0; i < assets.length; i++) {
      var a = assets[i];
      var matchName = a.name && a.name.toLowerCase().indexOf(searchLower) >= 0;
      var matchType = a.asset_type && a.asset_type.toLowerCase().indexOf(searchLower) >= 0;
      var matchSearch = matchName || matchType;
      var matchProperty = filterProperty === "all" || a.property_id === filterProperty;
      var matchUnit = filterUnit === "all" || a.unit_id === filterUnit;
      if (matchSearch && matchProperty && matchUnit) result.push(a);
    }
    return result;
  }

  function renderFilterPropertyOptions() {
    var items = [<SelectItem key="all" value="all">{t("common.all")}</SelectItem>];
    for (var i = 0; i < properties.length; i++) {
      items.push(<SelectItem key={properties[i].property_id} value={properties[i].property_id}>{properties[i].name}</SelectItem>);
    }
    return items;
  }

  function renderFilterUnitOptions() {
    var items = [<SelectItem key="all" value="all">{t("common.all")}</SelectItem>];
    for (var i = 0; i < filterUnits.length; i++) {
      items.push(<SelectItem key={filterUnits[i].unit_id} value={filterUnits[i].unit_id}>{filterUnits[i].name}</SelectItem>);
    }
    return items;
  }

  function renderPropertyOptions() {
    var items = [<SelectItem key="none" value="none">{t("common.none")}</SelectItem>];
    for (var i = 0; i < properties.length; i++) {
      items.push(<SelectItem key={properties[i].property_id} value={properties[i].property_id}>{properties[i].name}</SelectItem>);
    }
    return items;
  }

  function renderUnitOptions() {
    var items = [<SelectItem key="none" value="none">{t("common.none")}</SelectItem>];
    for (var i = 0; i < units.length; i++) {
      items.push(<SelectItem key={units[i].unit_id} value={units[i].unit_id}>{units[i].name}</SelectItem>);
    }
    return items;
  }

  function renderAssetTypeOptions() {
    var items = [];
    for (var i = 0; i < assetTypes.length; i++) {
      items.push(<SelectItem key={assetTypes[i]} value={assetTypes[i]}>{t("assets.types." + assetTypes[i])}</SelectItem>);
    }
    return items;
  }

  function renderConditionOptions() {
    var items = [];
    for (var i = 0; i < conditions.length; i++) {
      items.push(<SelectItem key={conditions[i]} value={conditions[i]}>{t("assets.conditions." + conditions[i])}</SelectItem>);
    }
    return items;
  }

  function renderAssetRows() {
    var filteredAssets = getFilteredAssets();
    var rows = [];
    for (var i = 0; i < filteredAssets.length; i++) {
      var asset = filteredAssets[i];
      var assetId = asset.asset_id;
      var assetData = JSON.parse(JSON.stringify(asset));
      rows.push(
        <TableRow key={assetId} data-testid={"asset-row-" + assetId}>
          <TableCell className="font-medium">{asset.name}</TableCell>
          <TableCell><Badge variant="outline">{t("assets.types." + asset.asset_type)}</Badge></TableCell>
          <TableCell>{getPropertyName(asset.property_id)}</TableCell>
          <TableCell>{asset.unit_id ? <Badge variant="secondary">{getUnitName(asset.unit_id)}</Badge> : "-"}</TableCell>
          <TableCell>{getConditionBadge(asset.condition)}</TableCell>
          <TableCell>{asset.purchase_price ? formatPrice(asset.purchase_price) : "-"}</TableCell>
          {isAdmin && (
            <TableCell>
              {asset.owner_name ? (
                <div>
                  <div className="font-medium text-sm">{asset.owner_name}</div>
                  <div className="text-xs text-muted-foreground">{asset.owner_email}</div>
                </div>
              ) : "-"}
            </TableCell>
          )}
          {canEdit && (
            <TableCell>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" onClick={handleEdit.bind(null, assetData)}><Pencil className="w-4 h-4" /></Button>
                <Button variant="ghost" size="icon" onClick={handleDelete.bind(null, assetId)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
              </div>
            </TableCell>
          )}
        </TableRow>
      );
    }
    return rows;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  var filteredAssets = getFilteredAssets();

  return (
    <div className="space-y-6" data-testid="assets-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("assets.title")}</h1>
          <p className="text-muted-foreground mt-1">{t("assets.subtitle")}</p>
        </div>
        {canEdit && (
          <Dialog open={dialogOpen} onOpenChange={function(open) { setDialogOpen(open); if (!open) { setEditingAsset(null); setForm(emptyForm); setUnits([]); } }}>
            <DialogTrigger asChild>
              <Button data-testid="add-asset-btn"><Plus className="w-4 h-4 mr-2" />{t("assets.addAsset")}</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{editingAsset ? t("assets.editAsset") : t("assets.addNewAsset")}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="form-label">{t("assets.assetName")}</Label>
                  <Input value={form.name} onChange={function(e) { setForm({ ...form, name: e.target.value }); }} placeholder="e.g., Samsung Refrigerator" required data-testid="asset-name-input" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="form-label">{t("assets.assetType")}</Label>
                    <Select value={form.asset_type} onValueChange={function(v) { setForm({ ...form, asset_type: v }); }}>
                      <SelectTrigger data-testid="asset-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{renderAssetTypeOptions()}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="form-label">{t("assets.condition")}</Label>
                    <Select value={form.condition} onValueChange={function(v) { setForm({ ...form, condition: v }); }}>
                      <SelectTrigger data-testid="asset-condition-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{renderConditionOptions()}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="form-label">{t("assets.property")}</Label>
                    <Select value={form.property_id || "none"} onValueChange={function(v) { setForm({ ...form, property_id: v, unit_id: "" }); }}>
                      <SelectTrigger data-testid="asset-property-select"><SelectValue placeholder={t("common.optional")} /></SelectTrigger>
                      <SelectContent>{renderPropertyOptions()}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="form-label">{t("common.unit")}</Label>
                    <Select value={form.unit_id || "none"} onValueChange={function(v) { setForm({ ...form, unit_id: v }); }} disabled={!form.property_id || form.property_id === "none" || units.length === 0}>
                      <SelectTrigger data-testid="asset-unit-select"><SelectValue placeholder={t("common.optional")} /></SelectTrigger>
                      <SelectContent>{renderUnitOptions()}</SelectContent>
                    </Select>
                    {form.property_id && form.property_id !== "none" && units.length === 0 && (
                      <p className="text-xs text-muted-foreground mt-1">{t("common.noUnitsAvailable")}</p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="form-label">{t("assets.purchaseDate")}</Label>
                    <Input type="date" value={form.purchase_date} onChange={function(e) { setForm({ ...form, purchase_date: e.target.value }); }} data-testid="asset-purchase-date-input" />
                  </div>
                  <div>
                    <Label className="form-label">{t("assets.purchasePrice")}</Label>
                    <Input type="number" value={form.purchase_price} onChange={function(e) { setForm({ ...form, purchase_price: e.target.value }); }} placeholder="0" data-testid="asset-purchase-price-input" />
                  </div>
                </div>
                <div>
                  <Label className="form-label">{t("assets.serialNumber")}</Label>
                  <Input value={form.serial_number} onChange={function(e) { setForm({ ...form, serial_number: e.target.value }); }} placeholder={t("common.optional")} data-testid="asset-serial-input" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="form-label">{t("assets.supplier")}</Label>
                    <Select value={form.supplier_id || "none"} onValueChange={function(v) { setForm({ ...form, supplier_id: v }); }}>
                      <SelectTrigger data-testid="asset-supplier-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{renderSupplierOptions()}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="form-label">{t("assets.maintenanceParty")}</Label>
                    <Input value={form.maintenance_party} onChange={function(e) { setForm({ ...form, maintenance_party: e.target.value }); }} placeholder={t("common.optional")} data-testid="asset-maintenance-party-input" />
                  </div>
                </div>
                <div>
                  <Label className="form-label">{t("common.description")}</Label>
                  <Textarea value={form.description} onChange={function(e) { setForm({ ...form, description: e.target.value }); }} placeholder={t("common.description")} rows={2} data-testid="asset-description-input" />
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={function() { setDialogOpen(false); }}>{t("common.cancel")}</Button>
                  <Button type="submit" data-testid="asset-submit-btn">{editingAsset ? t("common.update") : t("common.create")}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder={t("common.search")} value={search} onChange={function(e) { setSearch(e.target.value); }} className="pl-10" data-testid="asset-search-input" />
        </div>
        <Select value={filterProperty} onValueChange={function(v) { setFilterProperty(v); setFilterUnit("all"); }}>
          <SelectTrigger className="w-48" data-testid="asset-filter-property"><SelectValue placeholder={t("assets.property")} /></SelectTrigger>
          <SelectContent>{renderFilterPropertyOptions()}</SelectContent>
        </Select>
        <Select value={filterUnit} onValueChange={setFilterUnit} disabled={filterProperty === "all" || filterUnits.length === 0}>
          <SelectTrigger className="w-48" data-testid="asset-filter-unit"><SelectValue placeholder={t("common.unit")} /></SelectTrigger>
          <SelectContent>{renderFilterUnitOptions()}</SelectContent>
        </Select>
      </div>

      {filteredAssets.length > 0 ? (
        <Card className="bento-card overflow-hidden">
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>{t("common.name")}</TableHead>
                <TableHead>{t("common.type")}</TableHead>
                <TableHead>{t("assets.property")}</TableHead>
                <TableHead>{t("common.unit")}</TableHead>
                <TableHead>{t("assets.condition")}</TableHead>
                <TableHead>{t("assets.purchasePrice")}</TableHead>
                {isAdmin && <TableHead>{t("common.owner")}</TableHead>}
                {canEdit && <TableHead className="w-20">{t("common.actions")}</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>{renderAssetRows()}</TableBody>
          </Table>
        </Card>
      ) : (
        <Card className="bento-card">
          <CardContent className="py-12 text-center text-muted-foreground">
            <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>{t("assets.noAssets")}</p>
            {canEdit && <Button className="mt-4" onClick={function() { setDialogOpen(true); }}>{t("assets.addFirstAsset")}</Button>}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
